import {Component, Input, OnInit, ViewChild, ViewEncapsulation} from "@angular/core";
import {BusinessDto} from "../../model/business/dto/business-dto";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {MatSort, Sort} from "@angular/material/sort";
import {Form, FormControl, FormGroup} from "@angular/forms";
import {OrderHistoryService} from "../../pages/payment/order-history/order-history.service";
import {CustomerOrderDto} from "../../model/order/customer-order-dto.model";
import {TranslateService} from "@ngx-translate/core";
import {AlertService} from "../../services/alert/alert.service";
import {AnalyticsService} from "../../services/analytics/analytics.service";

@Component({
  selector: "app-order-history",
  templateUrl: "order-history.component.html",
  styleUrls: ["./order-history.component.scss"],
  encapsulation: ViewEncapsulation.None
})
export class OrderHistoryComponent implements OnInit {

  @Input() businessId: string;
  @Input() isInnerComponent?: boolean;

  USE_MOCK_DATA = false;

  public loadingPage = false;
  DEFAULT_PAGE_SIZE = 10;

  commandHistoryList: any[];
  commandHistoryListServer: CustomerOrderDto[];
  tempSortedArray: any[] = null;

  sortedData: any[];

  paginationOptions = {
    length: this.DEFAULT_PAGE_SIZE,
    pageSize: this.DEFAULT_PAGE_SIZE,
    pageIndex: 0,
    pageSizeOptions: [5, 10, 25, 100]
  };

  startDate: FormControl;
  endDate: FormControl;
  statusSelect: FormControl;

  startDateModel: Date;
  endDateModel: Date;

  lastSortOptions = {
    active: null,
    direction: null
  };

  statusList = ["", ...this.orderHistoryService.COMMAND_STATUS, "CASHED", "UN-CASHED"];
  totalAmount = 0;
  totalOrders = 0;

  constructor(
    private analyticsService: AnalyticsService,
    private alertService: AlertService,
    private translate: TranslateService,
    private orderHistoryService: OrderHistoryService) {

  }

  ngOnInit() {
    const oneWeekAgo = new Date();
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    if (this.isInnerComponent) {
      this.startDateModel = oneWeekAgo;
      this.endDateModel = new Date();
    } else {
      this.startDate = new FormControl(oneWeekAgo);
      this.endDate = new FormControl(new Date());
      this.statusSelect = new FormControl(null);
    }

    if (this.USE_MOCK_DATA) {
      this.commandHistoryList = this.orderHistoryService.getMockCommandHistory();
      this.paginationOptions.length = this.commandHistoryList.length;
      this.sortedData = this.commandHistoryList.slice(0, this.paginationOptions.pageSize);
      this.applyFilter();
    } else {
      this.fetchOrderHistory(false);
    }
  }

  public fetchOrderHistory(displaySuccessMessage?: boolean) {
    const startDateParam = this.isInnerComponent ? this.startDateModel : this.startDate.value;
    const endDateParam = this.isInnerComponent ? this.endDateModel : this.endDate.value;

    this.loadingPage = true;
    this.orderHistoryService.getOrderHistory(this.businessId,
      this.analyticsService.getDateAsString(startDateParam), this.analyticsService.getDateAsString(endDateParam)).subscribe(
      next => {
        this.loadingPage = false;
        if (next && next._body) {
          this.commandHistoryListServer = JSON.parse(next._body);
          this.commandHistoryList = [];
          for (let i = 0; i < this.commandHistoryListServer.length; i++) {
            const customerOrderDto: CustomerOrderDto = this.commandHistoryListServer[i];
            this.commandHistoryList.push({
              datePlaced: customerOrderDto.datePlaced,
              totalPrice: customerOrderDto.totalPrice / 100,
              currency: customerOrderDto.currency,
              translatedStatus: this.translate.instant('page.dashboard.analytics.orderHistory.status.' + customerOrderDto.orderStatus),
              orderStatus: customerOrderDto.orderStatus,
              tableLabel: customerOrderDto.tableLabel,
              customerEmail: customerOrderDto.customerEmail,
              orderItems: customerOrderDto.orderItems,
              settled: customerOrderDto.settled
            });
          }
          this.paginationOptions.length = this.commandHistoryList.length;
          this.sortedData = this.commandHistoryList.slice(0, this.paginationOptions.pageSize);
          this.applyFilter();
          if (displaySuccessMessage) {
            this.alertService.toastSuccess(this.translate.instant("page.dashboard.analytics.orderHistory.message.loadSuccess"));
          }
        } else {
          this.alertService.toastError(this.translate.instant("page.dashboard.analytics.orderHistory.message.loadFail"));
          console.error(this.translate.instant("page.dashboard.analytics.orderHistory.message.loadFail")
            + " Error: " + JSON.stringify(next));
        }
      }, error => {
        this.loadingPage = false;
        this.alertService.toastError(this.translate.instant("page.dashboard.analytics.orderHistory.message.loadError"));
        console.error(this.translate.instant("page.dashboard.analytics.orderHistory.message.loadFail")
          + " Error: " + JSON.stringify(error));
      }
    );
  }

  public dateFilterChanged() {
    this.fetchOrderHistory(true);
  }

  public filterChanged() {
    this.fetchOrderHistory(true);
  }

  sortData(sort: Sort) {
    const data = this.tempSortedArray ? this.tempSortedArray.slice() : this.commandHistoryList.slice();

    this.lastSortOptions.active = sort.active;
    this.lastSortOptions.active = sort.active;
    this.lastSortOptions.direction = sort.direction;

    if (!sort.active || sort.direction === '') {
      this.sortedData = data.slice(
        this.paginationOptions.pageIndex * this.paginationOptions.pageSize,
        (this.paginationOptions.pageIndex * this.paginationOptions.pageSize) + this.paginationOptions.pageSize);
      return;
    }

    this.tempSortedArray = data.sort((a, b) => {
      const isAsc = sort.direction === 'asc';
      switch (sort.active) {
        case 'datePlaced': return compare(a.datePlaced, b.datePlaced, isAsc);
        case 'totalPrice': return compare(a.totalPrice, b.totalPrice, isAsc);
        case 'translatedStatus': return compare(a.translatedStatus, b.translatedStatus, isAsc);
        default: return 0;
      }
    });
    this.sortedData = this.tempSortedArray.slice(
      this.paginationOptions.pageIndex * this.paginationOptions.pageSize,
      (this.paginationOptions.pageIndex * this.paginationOptions.pageSize) + this.paginationOptions.pageSize)
  }

  paginatorChange(event?: PageEvent) {
    console.log(' event = ' + JSON.stringify(event));
    if (this.tempSortedArray) {
      this.sortedData = this.tempSortedArray.slice(event.pageIndex * event.pageSize, (event.pageIndex * event.pageSize) + event.pageSize);
    } else {
      this.sortedData = this.commandHistoryList.slice(event.pageIndex * event.pageSize,
        (event.pageIndex * event.pageSize) + event.pageSize);
    }
    this.paginationOptions.pageSize = event.pageSize;
    this.paginationOptions.pageIndex = event.pageIndex;
  }

  applyFilter() {
    this.totalAmount = 0;
    this.totalOrders = 0;

    const startDate = this.isInnerComponent ? this.startDateModel : this.startDate.value;
    const endDate = this.isInnerComponent ? this.endDateModel : this.endDate.value;
    const statusVal = this.isInnerComponent ? null : this.statusSelect.value;
    if (!startDate && !endDate) {
      this.tempSortedArray = null;
      this.sortData({active: this.lastSortOptions.active, direction: this.lastSortOptions.direction});
      return;
    }

    const tempElementsArray = this.commandHistoryList.slice();
    this.tempSortedArray = [];
    for (let i = 0; i < tempElementsArray.length; i++) {
      let isAllowed = true;
      if (this.USE_MOCK_DATA) {
        if (startDate && startDate.getTime() > tempElementsArray[i].date.getTime()) {
          isAllowed = false;
        }
        if (endDate && endDate.getTime() < tempElementsArray[i].date.getTime()) {
          isAllowed = false;
        }
      }
      if (statusVal) {
        if (statusVal === "UN-CASHED") {
          if (tempElementsArray[i].settled === true) {
            isAllowed = false;
          }
        } else if (statusVal === "CASHED") {
          if (tempElementsArray[i].settled === false) {
            isAllowed = false;
          }
        } else if (statusVal !== tempElementsArray[i].orderStatus) {
          isAllowed = false;
        }
      }
      if (isAllowed) {
        this.tempSortedArray.push(tempElementsArray[i]);
        this.totalAmount += tempElementsArray[i].totalPrice;
        this.totalOrders++;
      }
    }
    this.paginationOptions.length = this.tempSortedArray.length;
    this.paginationOptions.pageIndex = 0;
    this.sortedData = this.tempSortedArray.slice(
      this.paginationOptions.pageIndex * this.paginationOptions.pageSize,
      (this.paginationOptions.pageIndex * this.paginationOptions.pageSize) + this.paginationOptions.pageSize);
    this.sortData({active: this.lastSortOptions.active, direction: this.lastSortOptions.direction});
  }
}

function compare(a: number | string, b: number | string, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
